/* eslint-disable */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Divider,
  CircularProgress,
  Button,
  TextField,
  IconButton,
} from '@mui/material'
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material'
import { Header } from '../../Components/Header'
import { useAuth } from '../../Contexts/AuthContext'
import { useToast } from '../../context/ToastContext'
import { authAPI } from '../../services/api'

export function MyProfile() {
  const navigate = useNavigate()
  const { user, userProfile, isLoggedIn, logout } = useAuth()
  const roleId = user?.role_id ?? user?.roleId ?? userProfile?.role_id ?? userProfile?.roleId
  const isAdmin = Number(roleId) === 1
  const toast = useToast()
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [profileData, setProfileData] = useState(null)
  const [editedData, setEditedData] = useState({})

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      setLoading(true)
      
      // Try to get profile from API
      try {
        const response = await authAPI.getProfile()
        const userData = response.data.data?.user || response.data.user || response.data.data || response.data
        setProfileData(userData)
      } catch (apiError) {
        // If API fails, use data from localStorage/context
        const storedUser = localStorage.getItem('user')
        const userData = localStorage.getItem('userData')
        
        if (storedUser) {
          const parsed = JSON.parse(storedUser)
          setProfileData(parsed.user || parsed)
        } else if (userData) {
          const parsed = JSON.parse(userData)
          setProfileData(parsed.user || parsed)
        } else if (userProfile) {
          setProfileData(userProfile.user || userProfile)
        } else if (user) {
          setProfileData(user.user || user)
        }
      }
    } catch (err) {
      console.error('Error fetching profile:', err)
      toast.showError('Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    setEditedData({ ...profileData })
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditedData({})
  }

  const handleSave = async () => {
    try {
      const emailChanged = editedData.email !== profileData.email
      
      const response = await authAPI.updateProfile(editedData)
      const updatedUserData = response.data.data?.user || response.data.user || editedData
      
      setProfileData(updatedUserData)
      setIsEditing(false)
      
      // Update all localStorage keys with user data
      const updatedUser = { user: updatedUserData }
      localStorage.setItem('user', JSON.stringify(updatedUser))
      
      // Also update userData if it exists
      if (localStorage.getItem('userData')) {
        localStorage.setItem('userData', JSON.stringify(updatedUser))
      }
      
      // If email changed, sign out user and ask them to re-login
      if (emailChanged) {
        toast.showSuccess('Email updated successfully. Please login again with your new email.')
        setTimeout(() => {
          logout()
          navigate('/login')
        }, 2000)
      } else {
        toast.showSuccess('Profile updated successfully')
      }
    } catch (err) {
      console.error('Error updating profile:', err)
      toast.showError(err.response?.data?.message || 'Failed to update profile')
    }
  }

  const handleChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }))
  }

  const handleListPropertyClick = () => {
    navigate('/my-properties')
  }

  const handleSignIn = () => {
    navigate('/login')
  }

  const handleSignOut = () => {
    logout()
    navigate('/')
  }

  const handleHomeClick = () => {
    navigate('/')
  }

  const handleMyPropertiesClick = () => {
    navigate('/my-properties')
  }

  const handleManageMyPropertiesClick = () => {
    navigate('/property-list')
  }

  const handleSavedPropertiesClick = () => {
    navigate('/saved-properties')
  }

  const handleMyProfileClick = () => {
    navigate('/my-profile')
  }

  const handleSearch = (query) => {
    if (!query || query.trim() === '') {
      sessionStorage.removeItem('propertySearchQuery')
      sessionStorage.setItem('propertyIsSearchMode', 'false')
      navigate('/')
    } else {
      const normalizedQuery = query.trim()
      sessionStorage.setItem('propertySearchQuery', normalizedQuery)
      sessionStorage.setItem('propertyIsSearchMode', 'true')
      navigate('/', {
        state: {
          seoSearchQuery: normalizedQuery,
        },
      })
    }
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={60} />
      </Box>
    )
  }

  if (!profileData) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Container sx={{ py: 8 }}>
          <Typography variant="h4">Profile not found</Typography>
          <Button onClick={() => navigate('/')} sx={{ mt: 2 }}>
            Back to Home
          </Button>
        </Container>
      </Box>
    )
  }

  const displayData = isEditing ? editedData : profileData
  const getInitials = () => {
    const firstName = displayData.firstName || ''
    const lastName = displayData.lastName || ''
    if (firstName && lastName) {
      return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
    }
    const name = displayData.user_name || displayData.name || displayData.email || ''
    return name.charAt(0).toUpperCase()
  }

  const getFullName = () => {
    const firstName = displayData.firstName || ''
    const lastName = displayData.lastName || ''
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim()
    }
    return displayData.user_name || displayData.name || 'User'
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box 
        component="main"
        sx={{ 
          display: "flex", 
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <Header
          onHomeClick={handleHomeClick}
          onListPropertyClick={handleListPropertyClick}
          onMyPropertiesClick={handleMyPropertiesClick}
          onManageMyPropertiesClick={handleManageMyPropertiesClick}
          onUnderVerificationClick={() => navigate('/all-properties')}
          onUsersClick={() => navigate('/users')}
          onSavedPropertiesClick={handleSavedPropertiesClick}
          onMyProfileClick={handleMyProfileClick}
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          userEmail={user?.email || userProfile?.email}
          onSignIn={handleSignIn}
          onSignOut={handleSignOut}
          onSearch={handleSearch}
        />
        <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            My Profile
          </Typography>
          {!isEditing ? (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit Profile
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
              >
                Save
              </Button>
            </Box>
          )}
        </Box>

        <Card>
          <CardContent sx={{ p: 4 }}>
            {/* Profile Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: 'primary.main',
                  fontSize: '2.5rem',
                  mr: 3
                }}
              >
                {getInitials()}
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {getFullName()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {displayData.email || 'No email provided'}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Profile Details */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Personal Information
            </Typography>

            <Grid container spacing={4}>
              {/* First Name */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    First Name:
                  </Typography>
                  {isEditing ? (
                    <TextField
                      size="small"
                      value={editedData.firstName || ''}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      sx={{ flex: 1 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {displayData.firstName || 'N/A'}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Last Name */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Last Name:
                  </Typography>
                  {isEditing ? (
                    <TextField
                      size="small"
                      value={editedData.lastName || ''}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      sx={{ flex: 1 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {displayData.lastName || 'N/A'}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Email */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <EmailIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Email:
                  </Typography>
                  {isEditing ? (
                    <TextField
                      size="small"
                      type="email"
                      value={editedData.email || ''}
                      onChange={(e) => handleChange('email', e.target.value)}
                      sx={{ flex: 1 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {displayData.email || 'N/A'}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Phone */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PhoneIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Phone:
                  </Typography>
                  {isEditing ? (
                    <TextField
                      size="small"
                      value={editedData.phone || ''}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      sx={{ flex: 1 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {displayData.phone || 'N/A'}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Additional fields if available */}
              {displayData.created_at && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Member Since:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {new Date(displayData.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Container>
      </Box>
    </Box>
  )
}
